import {
  useBusinessOverviewOffersQuery,
  useBusinessOverviewStatsQuery,
  useGetOfferScorecardsForOrgQuery,
} from '@client/graphql/__generated__/main-operations';

import {
  BusinessOverviewMetric,
  BusinessOverviewOfferStatusFilter,
  BusinessOverviewPeriod,
} from '@client/graphql/__generated__/types';
import { useInfiniteQuery } from '~/services/document/hooks';
import { useAppContext } from '~/services/main/contexts/AppContext';

export const useOrganizationPipelineQueries = ({
  selectedDateRange,
  selectedTab,
}: {
  selectedDateRange: BusinessOverviewPeriod;
  selectedTab: BusinessOverviewMetric;
}) => {
  const { selectedOrganization } = useAppContext();

  const {
    data: businessOverviewStatsData,
    loading: businessOverviewStatsLoading,
    error: businessOverviewStatsError,
  } = useBusinessOverviewStatsQuery({
    variables: {
      input: {
        period: selectedDateRange,
      },
    },
    skip: !selectedOrganization,
  });

  const [
    fetchOfferScorecardsTriggerRef,
    {
      data: offerScorecardsData,
      loading: offerScorecardsLoading,
      error: offerScorecardsError,
    },
  ] = useInfiniteQuery(useGetOfferScorecardsForOrgQuery, {
    variables: {
      filters: {
        period: selectedDateRange,
      },
    },
    skip:
      !selectedOrganization ||
      selectedTab !== BusinessOverviewMetric.OFFER_SCORECARDS,
  });

  const [
    fetchBusinessOverviewOffersTriggerRef,
    {
      data: businessOverviewOffersData,
      loading: businessOverviewOffersLoading,
      error: businessOverviewOffersError,
    },
  ] = useInfiniteQuery(useBusinessOverviewOffersQuery, {
    variables: {
      filters: {
        period: selectedDateRange,
        status:
          selectedTab === BusinessOverviewMetric.OFFERS_OUT
            ? BusinessOverviewOfferStatusFilter.OFFERS_OUT
            : BusinessOverviewOfferStatusFilter.OFFERS_ACCEPTED,
      },
    },
    skip:
      !selectedOrganization ||
      (selectedTab !== BusinessOverviewMetric.OFFERS_OUT &&
        selectedTab !== BusinessOverviewMetric.OFFERS_ACCEPTED),
  });

  return {
    summary: {
      data: businessOverviewStatsData?.businessOverviewStats.stats,
      loading: businessOverviewStatsLoading,
      error: businessOverviewStatsError,
    },
    offerScorecards: {
      data: offerScorecardsData?.offerScorecardsForOrg?.results,
      loading: offerScorecardsLoading,
      error: offerScorecardsError,
      fetchTriggerRef: fetchOfferScorecardsTriggerRef,
    },
    businessOverviewOffers: {
      data: businessOverviewOffersData?.businessOverviewOffers?.results,
      loading: businessOverviewOffersLoading,
      error: businessOverviewOffersError,
      fetchTriggerRef: fetchBusinessOverviewOffersTriggerRef,
    },
  };
};
